import * as Sentry from '@sentry/vue';

import { type DeploymentBackup, DeploymentState } from '@clovyr/pollen';
import { BackupAPI } from '@clovyr/pollen/backup';
import { isClovyrCode } from '@clovyr/pollen/garden';
import {
  CLOVYR_CUSTOM_DOMAIN,
  CLOVYR_WNEXT_FQDN,
  collectImages,
  DeploymentMethod,
  type Manifest,
  NewValues,
} from '@clovyr/pollen/manifest';
import { getBuilderForManifest } from '@clovyr/pollen/manifest/configure';
import { writeAllFilesBase64, writeBase64File } from '@clovyr/pollen/manifest/configure/util';
import { upgradeInstanceConfigure, waitForInstance } from '@clovyr/pollen/remote_exec';
import {
  pullAllImages,
  restartApp,
  stopApp,
  upgradeClovyrServices,
} from '@clovyr/pollen/remote_exec/docker';
import { upgradeClovyrServicesK8S } from '@clovyr/pollen/remote_exec/kubernetes';

import { config } from '@/init/config';
import { usePollenStore } from '@/stores/pollen_store';
import { UpdateStatus, useUpdateStore } from '@/stores/update_store';
import { persist, rehydrate } from '@/stores/util';

import { useRemoteExec } from './useRemoteExec';

const POST_LAUNCH_TASK_LAST_CHECKS_KEY = 'clovyr/post-launch-task-last-checks';
const POST_LAUNCH_TASK_CHECK_INTERVAL = 24 * 60 * 60 * 1000;

type PostLaunchTaskLastChecks = Record<string, number>;

function shouldRunPostLaunchTasks(deploymentID: string): boolean {
  const lastChecks = rehydrate<PostLaunchTaskLastChecks>(POST_LAUNCH_TASK_LAST_CHECKS_KEY) || {};
  const now = Date.now();
  const lastCheck = lastChecks[deploymentID] || 0;
  if (now - lastCheck < POST_LAUNCH_TASK_CHECK_INTERVAL) {
    return false;
  }

  lastChecks[deploymentID] = now;
  persist(POST_LAUNCH_TASK_LAST_CHECKS_KEY, lastChecks);
  return true;
}

export const useAppVersion = (deploymentID: Ref<string> | string) => {
  const pollenStore = usePollenStore();

  const deployment = computed(() => {
    const id = typeof deploymentID === 'string' ? deploymentID : deploymentID.value;
    return pollenStore.getDeploymentByLibraryRoute(id)!;
  });

  const app = computed(() => {
    return pollenStore.getDeploymentApp(deployment.value)!;
  });

  /**
   * Checks the currently running version of the deployment against the latest available version.
   */
  const isUpdateAvailable = computed(() => {
    if (deployment.value?.appID && deployment.value.appID.includes('fedimint')) {
      // TODO: remove this once fedi gives us the green light for upgrades
      return false;
    }
    return (
      deployment.value?.appVersion &&
      app.value?.metadata?.version &&
      !isClovyrCode(app.value.metadata.id) &&
      deployment.value.appVersion !== app.value.metadata.version
    );
  });

  /**
   * Create a named backup of the current deployment.
   *
   * @param backupName
   */
  async function takeBackup(backupName: string) {
    const { creds, ensureHostLogin } = useRemoteExec(deployment.value);
    if (!(await ensureHostLogin())) {
      throw new Error('host login failed, skipping backup');
    }

    const snapshotID = await new BackupAPI(
      creds.value.host,
      creds.value.accessKey,
      deployment.value.appID,
    ).createSnapshot(['keep']);

    deployment.value.backups ||= [];
    deployment.value.backups.push({
      snapshotID,
      createdAt: Date.now(),
      name: backupName,
      appVersion: deployment.value.appVersion!,
    });
  }

  /**
   * Updates the deployment to run the given version of the app.
   *
   * Process:
   *
   *   1. Update app image settings from new manifest
   *   2. Write new config on host
   *   3. Pull new images
   *   4. Stop the app
   *   5. Take a snapshot
   *   6. Restore snapshot, if given
   *   7. Restart the app
   *
   * @param app
   * @param backup if given, restore this backup before starting the app
   * @param emitter
   */
  // eslint-disable-next-line @typescript-eslint/no-shadow
  async function runAppVersion(app: Manifest, backup?: DeploymentBackup) {
    deployment.value.state = DeploymentState.Updating;
    await pollenStore.pollen.putDeployment(deployment.value);

    const { addJob } = useUpdateStore();
    const job = addJob({
      manifest: app,
      deployment: deployment.value,
      status: UpdateStatus.Started,
    });

    try {
      // Update app image settings from new manifest.
      // For apps who's image/tag is configured via app settings, we need to update those.
      const images = collectImages(app);
      images.forEach((image) => {
        deployment.value.appSettings[image.id] = image.image;
      });
      await pollenStore.pollen.putDeployment(deployment.value);

      // generate settings from this app version
      // TODO: this is mainly for when we have a new app version with new settings
      // rolling back will need to be handled differently.
      const currentSettings = deployment.value.appSettings;
      const newSettings = NewValues(
        {},
        app,
        currentSettings[CLOVYR_WNEXT_FQDN] as string,
        currentSettings[CLOVYR_CUSTOM_DOMAIN] as string,
      );
      // merge into current settings (current takes precedence so we don't overrwite previously generated values)
      deployment.value.appSettings = { ...newSettings, ...currentSettings };

      // write new config on host
      const builder = getBuilderForManifest(app, DeploymentMethod.DockerCompose);
      const [, , files, traefikComposeFile] = builder.prepareConfigureData(
        app,
        deployment.value.appSettings,
      );

      const writeConfigScript = `
pushd /opt/clovyr/apps/${app.metadata.id}
${writeAllFilesBase64(files)}
pushd /opt/clovyr/traefik
${writeBase64File('docker-compose.yml', traefikComposeFile)}
  `;

      const { creds, execWithOutput, execWithAsyncOutput } = useRemoteExec(deployment.value);
      const res = await execWithOutput(writeConfigScript);
      if (import.meta.env.DEV) {
        console.log('write config output:\n', await res.text());
      }

      const { host, accessKey: key } = creds.value;

      // update traefik first
      const traefikPull = execWithAsyncOutput(
        `
  cd /opt/clovyr/traefik
  docker compose pull --quiet
  `,
      );

      // now pull & restart
      job.status = UpdateStatus.Pulling;
      await pullAllImages(host, key, deployment.value.appID);

      job.status = UpdateStatus.Stopping;
      await stopApp(host, key, deployment.value.appID);

      try {
        job.status = UpdateStatus.Snapshotting;
        const backupName = `Version ${deployment.value.appVersion} (before ${backup ? 'rollback' : 'update'} to ${app.metadata.version})`;
        await takeBackup(backupName);
      } catch (e) {
        console.warn('error taking snapshot before update:', e);
        if (import.meta.env.PROD) {
          // ignore in dev
          throw e;
        }
      }

      if (backup) {
        job.status = UpdateStatus.Restoring;
        await new BackupAPI(host, key, deployment.value.appID).restoreSnapshot(
          backup.snapshotID,
          true,
        );
      }

      try {
        job.status = UpdateStatus.Restarting;
        await restartApp(host, key, deployment.value.appID);
      } catch (e) {
        console.error('error restarting app after update:', e);
        throw e;
      }

      // finish traefik restart
      try {
        const traefikRes = await traefikPull;
        if (import.meta.env.DEV) {
          console.log('traefik pull output:\n', traefikRes.output);
        }
      } catch (e) {
        console.warn('error pulling traefik update:', e);
      }
      // must restart async because we will lose connectivity briefly
      const traefikRestartRes = await execWithAsyncOutput(
        `
  cd /opt/clovyr/traefik
  docker compose up --quiet-pull -d
  `,
      );
      if (import.meta.env.DEV) {
        console.log('traefik restart output:\n', traefikRestartRes.output);
      }
      await waitForInstance(`https://${host}/_leaf/ready`, key, 1000, 50, 200);

      // update version & save
      deployment.value.appVersion = app.metadata.version;
      deployment.value.state = DeploymentState.Running;
      await pollenStore.pollen.putDeployment(deployment.value);
    } catch (e) {
      Sentry.captureException(e);
      console.error(e);
      // just revert to running for now, user can likely just try again.
      deployment.value.state = DeploymentState.Running;
      await pollenStore.pollen.putDeployment(deployment.value);
    }

    job.status = UpdateStatus.Complete;
  }

  async function runPostLaunchTasks() {
    if (deployment.value.state !== DeploymentState.Running) {
      return;
    }
    if (!shouldRunPostLaunchTasks(deployment.value.id)) {
      return;
    }

    const { creds, ensureHostLogin } = useRemoteExec(deployment.value, app.value);
    if (!(await ensureHostLogin())) {
      console.warn('host login failed, skipping post-launch tasks');
      return;
    }

    const { appID } = deployment.value;
    const { host: fqdn, accessKey: key } = creds.value;

    try {
      await upgradeInstanceConfigure(fqdn, key, config.VITE_APP_ENV);

      if (isClovyrCode(app.value.metadata.id)) {
        void upgradeClovyrServicesK8S(fqdn, key);
      } else {
        void upgradeClovyrServices(fqdn, key, appID);
      }
    } catch {
      // just try again next time we load the library
    }
  }

  return {
    isUpdateAvailable,

    runAppVersion,
    runPostLaunchTasks,
  };
};
